import { MOS_PACKAGE } from '@mxa-argos/constants/mos';
import {
  KIOSK_REQUEST_INTERVAL_MS,
  KIOSK_TIMEOUT_MS,
  MANAGED_CONFIGURATION_TIMEOUT_MS,
  MARK_DELAY_MS,
  MOS_TIMEOUT_MS,
} from '@mxa-argos/modules/forja/constants/forja.constants';
import type { ForjaApk } from '@mxa-argos/modules/forja/interfaces/forja-apk.interface';
import type { ForjaPhase } from '@mxa-argos/modules/forja/interfaces/forja-phase.interface';
import type { ForjaPlanStep } from '@mxa-argos/modules/forja/interfaces/forja-plan-step.interface';
import type { ForjaStepResult } from '@mxa-argos/modules/forja/interfaces/forja-step-result.interface';
import type { ForjaDeviceSession } from '@mxa-argos/modules/forja/lib/engine/forja-device-session';
import { toForjaErrorCode, toForjaErrorDetail } from '@mxa-argos/modules/forja/lib/forja-error';
import {
  finalCheckStepName,
  finalCheckSteps,
} from '@mxa-argos/modules/forja/lib/provisioning/final-check';
import {
  installStep,
  installStepName,
} from '@mxa-argos/modules/forja/lib/provisioning/steps/install.steps';
import { launchMOSStep } from '@mxa-argos/modules/forja/lib/provisioning/steps/launch-mos.step';
import { phaseASteps } from '@mxa-argos/modules/forja/lib/provisioning/steps/phase-a.steps';
import { phaseBSteps } from '@mxa-argos/modules/forja/lib/provisioning/steps/phase-b.steps';
import { postKioskSteps } from '@mxa-argos/modules/forja/lib/provisioning/steps/post-kiosk.steps';
import { releaseSteps } from '@mxa-argos/modules/forja/lib/provisioning/steps/release.steps';
import type { Semaphore } from '@mxa-argos/modules/forja/lib/semaphore';
import { sleep } from '@mxa-argos/modules/forja/lib/sleep';
import {
  enableKioskMode,
  markForProduction,
  pushMOSManagedConfiguration,
} from '@mxa-argos/modules/forja/services/forja-ops.service';

const MARK_STEP_NAME = 'mark for production';
const PUSH_MANAGED_CONFIGURATION_STEP_NAME = 'push managed configuration';
const MANAGED_CONFIGURATION_STEP_NAME = 'wait for managed configuration';
const FINAL_CHECK_PHASE = 'FINAL CHECK (read-back)';

export class DeviceProvisioner {
  private readonly results: ForjaStepResult[] = [];
  private lastKioskRequestAtMillis = 0;

  constructor(
    private readonly device: ForjaDeviceSession,
    private readonly apks: ForjaApk[],
    private readonly installSlots: Semaphore,
  ) {}

  async run(): Promise<ForjaStepResult[]> {
    this.device.emit({ type: 'plan', steps: this.planSteps() });

    this.results.push(...(await this.device.runPhase('PHASE A (pre-install)', phaseASteps())));

    if (this.apks.length > 0) {
      this.results.push(
        ...(await this.device.runPhase(
          'INSTALL (sideload)',
          this.apks.map((apk) => installStep(apk, this.installSlots)),
        )),
      );
    }

    const wasMOSInstalled = await this.device.waitFor(
      'MOS_INSTALL',
      () => this.device.isPackageInstalled(MOS_PACKAGE),
      MOS_TIMEOUT_MS,
    );

    if (!wasMOSInstalled) {
      return this.finish({
        name: 'wait for MOS',
        status: 'FAILED',
        errorCode: 'MOS_INSTALL_TIMEOUT',
      });
    }

    this.results.push(...(await this.device.runPhase('PHASE B (post-MOS)', phaseBSteps())));
    this.results.push(...(await this.device.runPhase('LAUNCH MOS', [launchMOSStep()])));

    // POST-KIOSK takes the network away, so closing a defective device strands it: the run that
    // would fix it can no longer reach Proget to release the kiosk.
    if (this.results.some((result) => result.status === 'FAILED')) {
      return this.finish(null);
    }

    await sleep(MARK_DELAY_MS);

    const markResult = await this.runMarkForProduction();
    this.results.push(markResult);

    if (markResult.status === 'FAILED') {
      return this.finish(null);
    }

    // A canary MOS only gets its managed configuration once the mark brings the canary group: until
    // then Proget expects the production build and the agent rejects the sideloaded one.
    const managedConfigurationResult = await this.runManagedConfiguration();
    this.results.push(managedConfigurationResult);

    if (managedConfigurationResult.status === 'FAILED') {
      return this.finish(null);
    }

    const wasKioskEngaged = await this.device.waitFor(
      'KIOSK',
      () => this.engageKiosk(),
      KIOSK_TIMEOUT_MS,
    );

    if (!wasKioskEngaged) {
      return this.finish({
        name: 'wait for kiosk',
        status: 'FAILED',
        errorCode: 'KIOSK_TIMEOUT',
      });
    }

    this.results.push(...(await this.device.runPhase('POST-KIOSK', postKioskSteps())));
    this.results.push(...(await this.runFinalCheck()));

    return this.finish(null);
  }

  private planSteps(): ForjaPlanStep[] {
    const inPhase = (phase: ForjaPhase, names: string[]): ForjaPlanStep[] =>
      names.map((name) => ({ name, phase }));

    return [
      ...inPhase(
        'PHASE_A',
        phaseASteps().map((step) => step.name),
      ),
      ...inPhase('INSTALL', this.apks.map(installStepName)),
      ...inPhase(
        'PHASE_B',
        phaseBSteps().map((step) => step.name),
      ),
      ...inPhase('LAUNCH', [launchMOSStep().name]),
      ...inPhase('MARK', [MARK_STEP_NAME, MANAGED_CONFIGURATION_STEP_NAME]),
      ...inPhase(
        'POST_KIOSK',
        postKioskSteps().map((step) => step.name),
      ),
      ...inPhase('FINAL_CHECK', finalCheckSteps(this.apks).map(finalCheckStepName)),
      ...inPhase(
        'RELEASE',
        releaseSteps().map((step) => step.name),
      ),
    ];
  }

  private waitForManagedConfiguration(): Promise<boolean> {
    return this.device.waitFor(
      'MANAGED_CONFIGURATION',
      () => this.device.hasManagedConfiguration(),
      MANAGED_CONFIGURATION_TIMEOUT_MS,
    );
  }

  private async runManagedConfiguration(): Promise<ForjaStepResult> {
    const name = MANAGED_CONFIGURATION_STEP_NAME;
    this.device.emit({ type: 'step-start', name });

    if (await this.waitForManagedConfiguration()) {
      return this.recordResult({ name, status: 'OK' });
    }

    // The Proget agent records a failed managed configuration as applied and never retries it.
    if (!(await this.pushManagedConfiguration())) {
      return this.recordResult({
        name,
        status: 'FAILED',
        errorCode: 'MANAGED_CONFIGURATION_MISSING',
      });
    }

    if (!(await this.waitForManagedConfiguration())) {
      return this.recordResult({
        name,
        status: 'FAILED',
        errorCode: 'MANAGED_CONFIGURATION_MISSING',
      });
    }

    return this.recordResult({ name, status: 'APPLIED' });
  }

  private async pushManagedConfiguration(): Promise<boolean> {
    const name = PUSH_MANAGED_CONFIGURATION_STEP_NAME;
    this.device.emit({ type: 'step-start', name });

    try {
      await pushMOSManagedConfiguration(this.device.imei);
      this.recordResult({ name, status: 'APPLIED' });
      return true;
    } catch (error) {
      this.recordResult({
        name,
        status: 'FAILED',
        errorCode: toForjaErrorCode(error),
        errorDetail: toForjaErrorDetail(error),
      });
      return false;
    }
  }

  private recordResult(result: ForjaStepResult): ForjaStepResult {
    this.device.emit({ type: 'step-result', result });
    return result;
  }

  private async engageKiosk(): Promise<boolean> {
    if (await this.device.isKioskEngaged()) {
      return true;
    }

    if (Date.now() - this.lastKioskRequestAtMillis < KIOSK_REQUEST_INTERVAL_MS) {
      return false;
    }

    this.lastKioskRequestAtMillis = Date.now();

    // Proget rejects the request while the kiosk profile is not assigned yet, which is the normal
    // state of a first run until the production group lands, hence the retries.
    await enableKioskMode(this.device.imei).catch((error: unknown) => {
      console.warn(`${this.device.serial}: kiosk activation request rejected`, error);
    });

    return false;
  }

  private async runMarkForProduction(): Promise<ForjaStepResult> {
    const name = MARK_STEP_NAME;
    this.device.emit({ type: 'step-start', name });

    if (await this.device.isKioskEngaged()) {
      const result: ForjaStepResult = { name, status: 'OK' };
      this.device.emit({ type: 'step-result', result });
      return result;
    }

    try {
      await markForProduction(this.device.imei);

      const result: ForjaStepResult = { name, status: 'APPLIED' };
      this.device.emit({ type: 'step-result', result });
      return result;
    } catch (error) {
      const result: ForjaStepResult = {
        name,
        status: 'FAILED',
        errorCode: toForjaErrorCode(error),
        errorDetail: toForjaErrorDetail(error),
      };
      this.device.emit({ type: 'step-result', result });
      return result;
    }
  }

  private async runFinalCheck(): Promise<ForjaStepResult[]> {
    const steps = finalCheckSteps(this.apks);

    this.device.emit({ type: 'phase-start', phase: FINAL_CHECK_PHASE });

    const results: ForjaStepResult[] = [];

    for (const step of steps) {
      const name = finalCheckStepName(step);
      this.device.emit({ type: 'step-start', name });

      const wasVerified = await step.verify(this.device).catch(() => false);
      const result: ForjaStepResult = { name, status: wasVerified ? 'OK' : 'FAILED' };

      this.device.emit({ type: 'step-result', result });
      results.push(result);
    }

    return results;
  }

  // RELEASE runs here so that failed runs also revert the scaffolding: otherwise an aborted run
  // leaves the screen permanently awake and Settings parked in Developer Options.
  private async finish(lastResult: ForjaStepResult | null): Promise<ForjaStepResult[]> {
    if (lastResult) {
      this.device.emit({ type: 'step-result', result: lastResult });
      this.results.push(lastResult);
    }

    this.results.push(...(await this.device.runPhase('RELEASE', releaseSteps())));

    const failure = this.results.find((result) => result.status === 'FAILED');

    this.device.emit({
      type: 'finished',
      wasSuccessful: !failure,
      errorCode: failure?.errorCode,
    });

    return this.results;
  }
}
